import {
  ApeironBlueButton,
  ApeironInfinityDataGrid,
  InfinityDatasourceResult,
  NextImage,
  useAccountContext
} from '@apeiron/temp'
import { Box, styled } from '@mui/material'
import { ROUTE } from '@src/constants/route'
import { useVipPassContext } from '@src/contexts/battle/demo/VipPassContext'
import useGetClaimableRewardTypeInfosByRewardName from '@src/hooks/graphql/useGetClaimableRewardTypeInfosByRewardName'
import useGetCurrentBattlePassPrizeSummary from '@src/hooks/graphql/useGetCurrentBattlePassPrizeSummary'
import { RewardInfo } from '@src/types/reward'
import { ICellRendererParams } from 'ag-grid-community'
import { useRouter } from 'next/navigation'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const MainContainer = styled(Box)`
  width: 100%;

  border-radius: 10px;

  padding: 20px;

  display: flex;
  flex-direction: column;
  align-items: center;

  ${props => props.theme.breakpoints.up('res1024')} {
    background-color: #333333;

    padding: 20px;
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    padding: 0px;

    height: auto;
  }
`

const DataGrid = styled(ApeironInfinityDataGrid)`
  border-radius: 10px;

  .ag-row {
    border-bottom: 1px solid #27272a;
  }

  ${props => props.theme.breakpoints.up('res1024')} {
    .ag-column-first {
      padding-left: 30px;
    }

    .ag-column-last {
      padding-right: 30px;
    }
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    background-color: #333333;
    .ag-column-first {
      padding-left: 20px;
    }

    .ag-column-last {
      padding-right: 20px;
    }
  }
`

const RewardContainer = styled(Box)`
  width: 100%;

  display: flex;
  align-items: center;

  gap: 6px;
`

const RewardImage = styled(NextImage)`
  aspect-ratio: 1;

  ${props => props.theme.breakpoints.up('res1024')} {
    width: 30px;
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    width: 25px;
  }
`

const ClaimButtonContainer = styled(ApeironBlueButton)`
  ${props => props.theme.breakpoints.up('res1024')} {
    width: 410px;
    margin-top: 30px;
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    width: 100%;
    margin-top: 10px;
  }
`

const VipPassRewardSection = () => {
  const { t } = useTranslation()

  const router = useRouter()

  const { fetch: getPrizeSummary } = useGetCurrentBattlePassPrizeSummary()

  const { fetch: getClaimableRewardTypeInfosByRewardName } =
    useGetClaimableRewardTypeInfosByRewardName()

  const { isLoggedIn } = useAccountContext()

  const { infiniteDataGridRef } = useVipPassContext()

  const [disableButton, setDisableButton] = useState(false)

  const fetch = useCallback(async (): Promise<InfinityDatasourceResult> => {
    if (!isLoggedIn) {
      setDisableButton(true)

      return { data: [] }
    }

    const response = await getPrizeSummary()

    if (R.isNil(response)) {
      setDisableButton(true)

      return { data: [] }
    }

    const allAssetName = R.pluck('prizeAssetName', response)

    const allClaimableRewardInfos =
      await getClaimableRewardTypeInfosByRewardName(allAssetName)

    if (R.isNil(allClaimableRewardInfos)) {
      setDisableButton(true)

      return { data: [] }
    }

    const allClaimableRewardInfosWithAmount = RA.mapIndexed(
      (item: RewardInfo) => {
        const itemAmount = R.find(
          obj => obj.prizeAssetName === item.assetName,
          response
        )?.totalQty

        return {
          ...item,
          id: item.assetName,
          amount: itemAmount
        }
      },
      allClaimableRewardInfos
    )

    const data = R.sortBy(R.prop('name'), allClaimableRewardInfosWithAmount)

    setDisableButton(R.isEmpty(data))

    return { data }
  }, [getClaimableRewardTypeInfosByRewardName, getPrizeSummary, isLoggedIn])

  const columns = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: t('vip_pass.reward.header.reward'),
        headerClass: 'custom-ag-header-left',
        cellClass: 'custom-ag-cell-left',
        sortable: false,
        flex: 1,
        minWidth: 300,
        cellRenderer: (params: ICellRendererParams) => {
          if (R.isNil(params?.data?.name)) {
            return
          }

          return (
            <RewardContainer>
              <RewardImage src={params.data.image} />
              {params.data.name}
            </RewardContainer>
          )
        }
      },
      {
        field: 'address',
        headerName: t('vip_pass.reward.header.claimable'),
        headerClass: 'custom-ag-header-center',
        cellClass: 'custom-ag-cell-center',
        sortable: false,
        flex: 1,
        minWidth: 150,
        cellRenderer: (params: ICellRendererParams) => {
          if (R.isNotEmpty(params?.data?.address)) {
            return t('common.yes')
          } else {
            return t('common.no')
          }
        }
      },
      {
        field: 'amount',
        headerName: t('vip_pass.reward.header.amount'),
        headerClass: 'custom-ag-header-right',
        cellClass: 'custom-ag-cell-right',
        sortable: false,
        flex: 1,
        minWidth: 150
      }
    ]
  }, [t])

  const handleToClaimPage = useCallback(() => {
    router.push(ROUTE.REWARD_LIST)
  }, [router])

  return (
    <MainContainer>
      <DataGrid
        fetch={fetch}
        headerHeight={50}
        rowHeight={50}
        gridRef={infiniteDataGridRef}
        columnDefs={columns}
      />

      <ClaimButtonContainer
        onClick={handleToClaimPage}
        disabled={disableButton}
      >
        {t('vip_pass.reward.claim_button')}
      </ClaimButtonContainer>
    </MainContainer>
  )
}

export default VipPassRewardSection
